import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { Search, Loader2, User, CheckSquare, FileText, ShoppingCart, Truck, Hash } from 'lucide-react'
import { chatService } from '../../services/chatService'
import { avatarGradient, initials } from '../crm/pipeline/tokens'
import { titleCaseCompany } from '../../utils/formatters'

const ICONS = {
  user: User, task: CheckSquare, quote: FileText,
  sales_order: ShoppingCart, purchase_order: Truck, channel: Hash,
}
const HINT = "'@' Kişi · '@@' Görev · '@@@' Belge · '#' Kanal"
const DOC_KINDS = ['quote', 'sales_order', 'purchase_order']
const DOC_COLS = 'minmax(0,1.5fr) minmax(0,1.6fr) 78px 96px'
const CUR = { EUR: '€', USD: '$', TRY: '₺', TL: '₺' }
const fmtDate = (d) => {
  if (!d) return '—'
  const [y, m, dd] = String(d).slice(0, 10).split('-')
  return `${dd}.${m}.${String(y).slice(2)}`
}
const fmtAmount = (v, cur) => (v == null || v === 0
  ? '—'
  : `${CUR[cur] || ''}${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`)

/**
 * ClickUp mention paneli — sekmeli (Kişiler/Görevler/Teklifler/Siparişler/Satınalma/Kanallar).
 * Sekmeler role göre backend'den gelir; belge sekmeleri yetki filtresiyle döner
 * (SALES yalnız kendi saldept'i, satınalma yalnız yetkili roller).
 * Seçilen öğe `insert` metni olarak mesaja eklenir (mention / @@görev / markdown link).
 */
export default function MentionPanel({ company, anchor, initialKind = 'user', initialQuery = '', channelId = null, onPick, onClose }) {
  const [kind, setKind] = useState(initialKind)
  const [q, setQ] = useState(initialQuery)
  const debQ = useDebouncedValue(q, 350)
  const [idx, setIdx] = useState(0)
  const boxRef = useRef(null)

  const { data, isFetching } = useQuery({
    queryKey: ['chat-mention', company, kind, debQ, channelId || 0],
    queryFn: () => chatService.mentionSearch(company, kind, debQ, channelId),
    staleTime: 15000, placeholderData: keepPreviousData,
  })
  const items = data?.data?.items || []
  const isDoc = DOC_KINDS.includes(kind)   // belge sekmeleri tablo görünümünde
  const kinds = data?.data?.kinds || [{ key: 'user', label: 'Kişiler' }]
  useEffect(() => { setIdx(0) }, [kind, q, items.length])

  // Klavye: ↑↓ gez, Enter seç, Esc kapat (composer'ın kendi Enter'ını ezmek için capture)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose?.(); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); setIdx((i) => Math.min(i + 1, items.length - 1)) }
      else if (e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); setIdx((i) => Math.max(i - 1, 0)) }
      else if (e.key === 'Enter' && items[idx]) { e.preventDefault(); e.stopPropagation(); onPick?.(items[idx]) }
      else if (e.key === 'Tab') { e.preventDefault(); e.stopPropagation()
        const ki = kinds.findIndex((k) => k.key === kind)
        setKind(kinds[(ki + (e.shiftKey ? -1 : 1) + kinds.length) % kinds.length].key) }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [items, idx, kind, kinds, onPick, onClose])

  // Konum: rect mount'tan SONRA ölçülür (ilk render'da ref boş olabiliyor → panel sol alta düşüyordu)
  const [pos, setPos] = useState({ left: 24, bottom: 90, width: 420 })
  useLayoutEffect(() => {
    const measure = () => {
      const r = anchor?.getBoundingClientRect?.()
      if (!r || !r.width) return
      // Sekmeler (6 adet) tek satıra sığsın → yatay kaydırma çubuğu çıkmaz
      const w = Math.min(Math.max(r.width - 24, 560), window.innerWidth - 32)
      setPos({
        left: Math.max(12, Math.min(r.left + 12, window.innerWidth - w - 12)),
        bottom: window.innerHeight - r.top + 6,
        width: w,
      })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [anchor])

  return createPortal(
    <div className="fixed inset-0 z-[92]" onMouseDown={onClose}>
      <div ref={boxRef} className="absolute bg-white rounded-xl border border-gray-200 shadow-2xl flex flex-col overflow-hidden"
        style={{ left: pos.left, bottom: pos.bottom, width: pos.width, maxHeight: 380 }}
        onMouseDown={(e) => e.stopPropagation()}>

        <div className="flex flex-wrap items-center gap-x-0.5 px-2 pt-2 border-b border-gray-100">
          {kinds.map((k) => {
            const Icon = ICONS[k.key] || Hash
            return (
              <button key={k.key} onClick={() => setKind(k.key)}
                className={`flex items-center gap-1 px-2 py-1.5 text-[12px] font-medium border-b-2 -mb-px whitespace-nowrap
                  ${kind === k.key ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                <Icon size={13} /> {k.label}
              </button>
            )
          })}
        </div>

        <div className="px-2.5 pt-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara…"
              className="w-full pl-7 pr-2 py-1.5 text-[13px] border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-violet-200" />
          </div>
        </div>

        {isDoc && items.length > 0 && (
          <div className="grid gap-2 px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100"
            style={{ gridTemplateColumns: DOC_COLS }}>
            <span>Belge No</span><span>{kind === 'purchase_order' ? 'Tedarikçi' : 'Müşteri'}</span>
            <span className="text-right">Tarih</span><span className="text-right">Tutar</span>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-1.5">
          {isFetching && items.length === 0 && (
            <div className="py-6 text-center text-gray-400"><Loader2 size={16} className="animate-spin mx-auto" /></div>
          )}
          {!isFetching && items.length === 0 && (
            <div className="py-6 text-center text-[12px] text-gray-400">Sonuç yok</div>
          )}
          {isDoc && items.map((it, i) => (
            <button key={`${kind}-${it.id}`} onClick={() => onPick?.(it)} onMouseEnter={() => setIdx(i)}
              className={`w-full grid gap-2 items-center px-2 py-1.5 rounded-lg text-left ${i === idx ? 'bg-violet-50' : 'hover:bg-gray-50'}`}
              style={{ gridTemplateColumns: DOC_COLS }}>
              <span className="min-w-0">
                <span className="block text-[12.5px] font-medium text-gray-800 truncate">{it.label}</span>
                {(it.title || it.sub) && <span className="block text-[10.5px] text-gray-400 truncate">{it.title || it.sub}</span>}
              </span>
              <span className="text-[12px] text-gray-600 truncate" title={it.party || ''}>{titleCaseCompany(it.party) || '—'}</span>
              <span className="text-[11.5px] text-gray-500 tabular-nums text-right">{fmtDate(it.date)}</span>
              <span className="text-[12px] text-gray-800 tabular-nums text-right">{fmtAmount(it.amount, it.currency)}</span>
            </button>
          ))}
          {!isDoc && items.map((it, i) => (
            <button key={`${kind}-${it.id}`} onClick={() => onPick?.(it)} onMouseEnter={() => setIdx(i)}
              className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left ${i === idx ? 'bg-violet-50' : 'hover:bg-gray-50'}`}>
              {kind === 'user' ? (
                <span className={`w-6 h-6 rounded-full bg-gradient-to-br ${avatarGradient(it.label)} flex items-center justify-center text-[9px] font-semibold text-white shrink-0`}>
                  {initials(it.label)}
                </span>
              ) : (
                <span className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 shrink-0">
                  {(() => { const I = ICONS[kind] || Hash; return <I size={13} /> })()}
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] text-gray-800 truncate">{it.label}</span>
                {it.sub && <span className="block text-[11px] text-gray-400 truncate">{it.sub}</span>}
              </span>
            </button>
          ))}
        </div>

        <div className="px-3 py-1.5 border-t border-gray-100 text-[10px] text-gray-400">{HINT}</div>
      </div>
    </div>,
    document.body,
  )
}
